import { CancelToken } from "axios";
import { Form, Formik, useFormikContext } from "formik";
import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import clientApi from "../../api/client";
import { RightType } from "../../models/auth";
import { IClientNoteItem, IClientNoteWrite } from "../../models/client";
import { ExportType } from "../../models/common";
import {
  IInformationTypeAccess,
  InformationAccessLevel,
} from "../../models/informationType";
import { IApplicationState } from "../../store";
import { selectClientId } from "../../store/client";
import {
  changeClientNoteListFilter,
  changeClientNoteListOrder,
  changeClientNoteListPage,
  clientNoteListLoad,
  getClientNoteList,
  getClientNoteListCount,
  IClientNoteListState,
  selectClientNoteList,
} from "../../store/clientNoteList";
import { IFilterType } from "../../store/filterType";
import { selectIdentityRights } from "../../store/identity";
import { selectAvailableInformationTypes } from "../../store/informationType";
import { SpaceBetweenButtons, VerticalGap } from "../../styles/spaces";
import { hasRight } from "../../utils/rights";
import validations from "../../utils/validations";
import { DATE_FORMAT } from "../../utils/consts";
import {
  promiseToastDeleteNoException,
  promiseToastSaveNoException,
} from "../../utils/toasts";
import { format } from "date-fns";
import FilterPanel from "../common/filter/FilterPanel";
import Input from "../common/form/Input";
import Grid from "../common/grid/Grid";
import {
  GridIconAdd,
  GridIconDelete,
  GridIconEdit,
} from "../common/grid/GridIcons";
import { GridInlineButtons } from "../common/grid/GridInline";
import {
  TableCol,
  TableColExtraText,
  TableColNoWrap,
} from "../common/grid/TableCol";

interface IProps {
  prov: IClientNoteListState;
  clientId?: number;
  identityRights?: RightType[];
  informationTypes: IInformationTypeAccess[];
  getClientNoteList(clientId: number, cancelToken: CancelToken): void;
  getClientNoteListCount(clientId: number, cancelToken: CancelToken): void;
  changeClientNoteListOrder(orderBy: string, orderDesc: boolean): void;
  changeClientNoteListPage(page: number): void;
  changeClientNoteListFilter(filter: IFilterType): void;
  clientNoteListLoad(reload: boolean): void;
}

const ClientNoteGridEditor: FC<{
  data: IClientNoteItem;
  informationTypes: IInformationTypeAccess[];
  handleCancel(): void;
}> = ({ data, informationTypes, handleCancel }) => {
  const { errors, touched, isSubmitting, handleSubmit, resetForm } =
    useFormikContext<IClientNoteWrite>();

  useEffect(() => {
    const selectedInformationTypeId = informationTypes.some(
      (informationType) => informationType.id === data.informationType.id
    )
      ? data.informationType.id
      : informationTypes[0]?.id ?? 0;

    resetForm({
      values: {
        informationTypeId: selectedInformationTypeId,
        description: data.description,
      },
    });
  }, [data, informationTypes, resetForm]);

  return (
    <>
      <TableCol>{format(data.createdAt, DATE_FORMAT)}</TableCol>
      <TableColNoWrap>{data.creator?.name}</TableColNoWrap>
      <td colSpan={2}>
        <VerticalGap className="width100 nowrap">
          <Input
            name="informationTypeId"
            component="select"
            error={touched.informationTypeId && !!errors.informationTypeId}
          >
            {informationTypes.map((informationType) => (
              <option key={informationType.id} value={informationType.id}>
                {informationType.name}
              </option>
            ))}
          </Input>
          <Input
            name="description"
            component="textarea"
            error={touched.description && !!errors.description}
            inputHeight="10rem"
            maxLength={2050}
          />
        </VerticalGap>
      </td>
      <GridInlineButtons
        handleCancel={handleCancel}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />
    </>
  );
};

const ClientNote: FC<IProps> = ({
  prov,
  clientId,
  identityRights,
  informationTypes,
  getClientNoteList,
  getClientNoteListCount,
  changeClientNoteListOrder,
  changeClientNoteListPage,
  changeClientNoteListFilter,
  clientNoteListLoad,
}) => {
  const { t } = useTranslation();
  const [inlineEditId, setInlineEditId] = useState<number>();
  const [inlineAdd, setInlineAdd] = useState(false);

  const writableInformationTypes = useMemo(
    () =>
      informationTypes.filter(
        (informationType) =>
          informationType.accessLevel === InformationAccessLevel.ReadWrite
      ),
    [informationTypes]
  );

  const defaultData = useMemo<IClientNoteItem>(
    () => ({
      id: 0,
      informationType: {
        id: writableInformationTypes[0]?.id ?? 0,
        name: writableInformationTypes[0]?.name ?? "",
      },
      legacyTitle: null,
      description: "",
      createdAt: new Date(),
      creator: null,
      canEdit: true,
      canDelete: true,
    }),
    [writableInformationTypes]
  );

  useEffect(() => {
    clientNoteListLoad(false);
  }, [clientNoteListLoad]);

  const handleEdit = (id: number) => {
    setInlineAdd(false);
    setInlineEditId(id);
  };

  const handleCancel = () => {
    setInlineEditId(undefined);
    setInlineAdd(false);
  };

  const handleDelete = useCallback(
    async (id: number) => {
      await promiseToastDeleteNoException(async () => {
        await clientApi.deleteClientNote(id);
        clientNoteListLoad(true);
        handleCancel();
      });
    },
    [clientNoteListLoad]
  );

  const handleAdd = () => {
    setInlineEditId(undefined);
    setInlineAdd(true);
  };

  const handleRenderData = (item: IClientNoteItem): ReactElement => {
    return (
      <>
        <TableCol>{format(item.createdAt, DATE_FORMAT)}</TableCol>
        <TableColNoWrap>{item.creator?.name}</TableColNoWrap>
        <TableColExtraText
          text1={item.informationType?.name ?? item.legacyTitle}
          text2={item.description}
        />
        <TableCol>
          {!inlineEditId && !inlineAdd && (
            <>
              {item.canEdit && (
                <GridIconEdit onClick={() => handleEdit(item.id)} />
              )}
              {item.canEdit && item.canDelete && <SpaceBetweenButtons />}
              {item.canDelete && (
                <GridIconDelete onClick={() => handleDelete(item.id)} />
              )}
            </>
          )}
        </TableCol>
      </>
    );
  };

  const handleSubmit = async (data: IClientNoteWrite) => {
    await promiseToastSaveNoException(async () => {
      if (inlineEditId) {
        await clientApi.updateClientNote(inlineEditId, data);
      } else {
        await clientApi.createClientNote(clientId!, data);
      }
      clientNoteListLoad(true);
      handleCancel();
    });
  };

  const handleRenderEditor = (item: IClientNoteItem): ReactElement => (
    <ClientNoteGridEditor
      data={inlineAdd ? defaultData : item}
      informationTypes={writableInformationTypes}
      handleCancel={handleCancel}
    />
  );

  const handleGetData = (cancelToken: CancelToken) =>
    getClientNoteList(clientId!, cancelToken);

  const handleGetCount = (cancelToken: CancelToken) =>
    getClientNoteListCount(clientId!, cancelToken);

  const handleExport = async () => clientApi.exportClientNotes(clientId!);

  return (
    <>
      <FilterPanel
        viewFiltration={!inlineEditId && !inlineAdd}
        name="clientNote"
        filter={prov.filter!}
        changeFilter={changeClientNoteListFilter}
        doExport={
          hasRight(identityRights, [RightType.ExportClient])
            ? handleExport
            : undefined
        }
        exportTypes={[ExportType.Xlsx]}
      />
      <Formik<IClientNoteWrite>
        initialValues={{
          informationTypeId: writableInformationTypes[0]?.id ?? 0,
          description: "",
        }}
        validationSchema={Yup.object({
          informationTypeId: validations.idRequired(t),
          description: validations.stringRequired(t),
        })}
        validateOnMount={true}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid<IClientNoteItem>
            headers={[
              {
                captionStr: "client.info.createdAt",
                orderName: "CreatedAt",
                minContent: true,
              },
              {
                captionStr: "client.info.creator",
                orderName: "Creator",
                minContent: true,
              },
              {
                captionStr: "client.info.informationType",
                orderName: "InformationType",
                minContent: true,
              },
              {
                captionStr: "client.info.description",
                orderName: "Description",
              },
              {
                captionEl:
                  !inlineEditId &&
                  !inlineAdd &&
                  writableInformationTypes.length > 0 ? (
                    <GridIconAdd onClick={handleAdd} />
                  ) : undefined,
              },
            ]}
            inlineEditId={inlineEditId}
            inlineAdd={inlineAdd}
            defaultData={defaultData}
            renderData={handleRenderData}
            renderEditor={handleRenderEditor}
            getData={handleGetData}
            getCount={handleGetCount}
            changeOrder={changeClientNoteListOrder}
            changePage={changeClientNoteListPage}
            prov={prov}
            thirdColumnWidth="70vw"
            fourthColumnWidth="170vw"
          />
        </Form>
      </Formik>
    </>
  );
};

const mapStateToProps = (state: IApplicationState) => ({
  prov: selectClientNoteList(state),
  clientId: selectClientId(state),
  identityRights: selectIdentityRights(state),
  informationTypes: selectAvailableInformationTypes(state),
});

const mapDispachToProps = {
  getClientNoteList,
  getClientNoteListCount,
  changeClientNoteListOrder,
  changeClientNoteListPage,
  changeClientNoteListFilter,
  clientNoteListLoad,
};

export default connect(mapStateToProps, mapDispachToProps)(ClientNote);
